<template>
  <aside class="rs-sidebar">
    <header class="rs-sidebar__header">
      <div class="row">
        <div class="col-md-12">
          <div class="d-flex align-center">
            <h2>{{ badgeText }}</h2>
            <div v-if="sidebarTotal">
              <RsBadge
                class="ml-2"
                pill
                variant="light"
              >
                {{ sidebarTotal }}
              </RsBadge>
            </div>
            <RsButton
              v-if="isCreatePermitted"
              class="add-button ml-auto"
              variant="light"
              text
              icon="plus"
              title="Add"
              @click="$emit('on-add-click')"
            />
          </div>
        </div>
      </div>
      <!-- Post type selector -->
      <RsFormSelect
        v-if="showTypeSelect"
        v-model="selectValue"
        :options="selectOptions"
      />
      <div v-if="hasSearch">
        <RsFormInput
          ref="sidebarSearchBox"
          v-model="input"
          type="search"
          icon="search"
          :debounce="800"
        />
      </div>
    </header>
    <slot name="body" />
  </aside>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { ISelectOption } from '@/shared';

export default defineComponent({
  name: 'RsSidebar',

  props: {
    sidebarTotal: [String, Number],
    badgeText: String,
    value: String,
    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so
     * both `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input`
     * consumers work. `undefined` means the parent did not bind v-model, so we
     * fall back to `value` (see the `boundValue` computed).
     */
    modelValue: {
      type: String,
      default: undefined,
    },
    showTypeSelect: {
      type: Boolean,
      default: false,
    },
    selectOptions: {
      type: Array as PropType<ISelectOption[]>,
      default: () => [],
    },
    selectedOption: {
      type: String,
      default: '',
    },
    hasSearch: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['input', 'on-add-click', 'on-select-change', 'update:modelValue'],
  data() {
    return {
      input: `${this.modelValue !== undefined ? this.modelValue : this.value}`,
      selectValue: `${this.selectedOption}` || '',
    };
  },

  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): string | undefined {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
  },

  watch: {
    selectedOption() {
      this.selectValue = `${this.selectedOption}`;
    },

    selectValue() {
      this.$emit('on-select-change', this.selectValue);
    },

    boundValue() {
      this.input = `${this.boundValue}`;
    },

    input() {
      this.$emit('input', this.input);
      this.$emit('update:modelValue', this.input);
    },
  },
});
</script>
<style lang="scss">
@import "@/styles/_variables.scss";
.rs-sidebar {
  display: flex;
  position: sticky;
  top: 0rem;
  height: 100%;
  max-height: 100%;
  align-items: stretch;
  flex-shrink: 0;
  flex-direction: column;
  z-index: 99;
  overflow: hidden;
  background: $white;
  border-right: 1px solid $tertiary-gray;
  h2{
    margin: 0;
    display: inline-block;
    font-size: rem-calc(20);
  }
  .rs-search{
    .form-control{
      border-left-color: $white;
    }
  }

  .tab-nav {
    padding: 0;
    background: #f5f6fa;
    border-top: 1px solid #e5e9f2;
    border-bottom: 1px solid #e5e9f2;
    &__wrap{
      width: 100%;
    }

    &__item {
      &:nth-child(1){
        margin-left: 1.5rem;
      }
      span {
        padding: 1rem 0;
      }
    }
  }

  &__header {
    padding: 1.5rem 1.5rem 1.5rem 1.5rem;
    .rs-select{
      margin: 1rem 0;
    }
    .rs-input{
      margin: 1rem 0 0 0;
    }
    .add-button {
      margin: -0.25rem -0.25rem 0 0;
      width: 32px;
      height: 32px;
      color: rgba($tertiary-muted, 0.6);
      font-size: 1.25rem;
      &:hover{
        background-color: $secondary-color;
      }
    }
  }

  &__body {
    flex-grow: 1;
  }
  .scrollable-tab {
    height: 400px;
    max-height: 100%;
    overflow: auto;
  }
}
</style>
